import axios from 'axios';
import { Sentry } from './sentry.init';

import { JWT_HOST_API, API_KEY, API_SECRET_KEY } from 'configs/auth.config';
import { incrementLoader, decrementLoader } from './loaderManager';


const axiosInstance = axios.create({
  baseURL: JWT_HOST_API,
});

// Attach common headers before each request
axiosInstance.interceptors.request.use(
  (config) => {
    // ✅ Explicit default: show loader unless explicitly set to false
    const showLoader =
      typeof config.showLoader === 'boolean' ? config.showLoader : true;

    // Increment loader counter for this request only if showLoader is true
    if (showLoader) {
      incrementLoader();
    }

    // Store showLoader flag in config for use in response interceptor
    config._showLoader = showLoader;

    // Add Sentry breadcrumb for API request tracking
    Sentry.addBreadcrumb({
      category: "http",
      message: `${config.method?.toUpperCase()} ${config.url}`,
      level: "info",
      data: {
        url: config.url,
        method: config.method,
      },
    });

    const requestConfig = { ...config };

    requestConfig.baseURL = JWT_HOST_API;
    requestConfig.headers = requestConfig.headers || {};

    // Default headers
    if (!requestConfig.headers['Accept']) {
      requestConfig.headers['Accept'] = 'application/json';
    }

    // For FormData (file uploads), let the browser set Content-Type with proper boundary
    // Manually setting Content-Type for multipart/form-data breaks the boundary parameter
    if (requestConfig.data instanceof FormData) {
      // Delete Content-Type to let browser set it with correct boundary
      delete requestConfig.headers['Content-Type'];
      delete requestConfig.headers['content-type'];
    } else if (!requestConfig.headers['Content-Type']) {
      requestConfig.headers['Content-Type'] = 'application/json';
    }


    // Inject API keys if not already provided (backend expects X-API-KEY)
    if (API_KEY && !requestConfig.headers['X-API-KEY']) {
      requestConfig.headers['X-API-KEY'] = API_KEY;
    }
    if (API_SECRET_KEY && !requestConfig.headers['SECRET-KEY']) {
      requestConfig.headers['SECRET-KEY'] = API_SECRET_KEY;
    }

    // Optionally omit Authorization header (e.g., for GetCRMToken)
    const urlPath = typeof requestConfig.url === 'string' ? requestConfig.url : '';
    const shouldOmitAuth = requestConfig.omitAuth === true || urlPath.includes('/api/Account/GetCRMToken');
    if (!shouldOmitAuth) {
      const storedAuthToken = localStorage.getItem('authToken');
      if (storedAuthToken && !requestConfig.headers['Authorization']) {
        requestConfig.headers['Authorization'] = `Bearer ${storedAuthToken}`;
      }
    } else if (requestConfig.headers) {
      delete requestConfig.headers['Authorization'];
      delete requestConfig.headers['authorization'];
    }

    return requestConfig;
  },
  (error) => {
    // Decrement loader counter if request fails to send (only if showLoader was true)
    if (error?.config?._showLoader !== false) {
      decrementLoader();
    }
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    // Decrement loader counter on successful response only if showLoader was true
    if (response.config._showLoader !== false) {
      decrementLoader();
    }
    return response;
  },
  (error) => {
    // Decrement loader counter on error response only if showLoader was true
    if (error?.config?._showLoader !== false) {
      decrementLoader();
    }

    const response = error?.response;
    const normalizedError = {
      message: response?.data?.message || error?.message || 'Request failed',
      status: response?.status,
      statusCode: response?.data?.statusCode ?? response?.status,
      data: response?.data,
      url: error?.config?.url,
      method: error?.config?.method,
    };

    // Always log details so errors are visible during development and debugging
    console.error('API Error:', normalizedError);

    // Report HTTP errors to Sentry (skip 401/403/404 - typically auth/navigation issues)
    const status = normalizedError.status;
    if (status && ![401, 403, 404].includes(status)) {
      Sentry.captureException(new Error(`API Error: ${normalizedError.message}`), {
        tags: {
          errorType: "api_error",
          statusCode: String(status),
          endpoint: normalizedError.url,
        },
        extra: {
          method: normalizedError.method,
          responseData: normalizedError.data,
        },
      });
    }

    return Promise.reject(normalizedError);
  }
);

export default axiosInstance;
